import { useCurrentBox } from "@modules/box/hooks/useBox"
import { useServicesContext } from "@modules/core/services/services.context"
import { StripeAccountCreationErrorCode, StripeCountryCode, StripePlatform } from "@modules/payment/stripeTypes"
import { LoggerType } from "@modules/utils/loggerUtils"
import * as Sentry from "@sentry/react"
import { useSuspenseQuery } from "@tanstack/react-query"
import { useRef, useState } from "react"

export const useStripeAccount = () => {
  const [loading, setLoading] = useState(false)
  const { paymentService } = useServicesContext()
  const { result: boxId } = useCurrentBox()

  const createStripeAccount = async (countryCode: StripeCountryCode, stripePlatform: StripePlatform) => {
    try {
      setLoading(true)
      const accountToken = await paymentService.createAccountToken(stripePlatform)
      if (accountToken.error || !accountToken.id) {
        console.error(LoggerType.Stripe + "create account token", accountToken.error?.code)
        throw Error("Stripe create account failure")
      }

      let result
      if (boxId) {
        result = await paymentService.createBoxStripeAccount(accountToken.id, countryCode, stripePlatform)
      } else if (countryCode) {
        result = await paymentService.createUserStripeAccount(accountToken.id, countryCode, stripePlatform)
      } else throw new Error("Missing country code")

      if (result && result.url) {
        window.open(result.url, "_self")
        return
      }
    } catch (e: unknown) {
      if (
        typeof e === "object" &&
        e !== null &&
        "response" in e &&
        e.response !== null &&
        typeof e.response === "object" &&
        "data" in e.response &&
        e.response.data !== null &&
        typeof e.response.data === "object" &&
        "code" in e.response.data &&
        "status" in e.response &&
        e.response.status === 409 &&
        (e.response.data.code === StripeAccountCreationErrorCode.STRIPE_ACCOUNT_ALREADY_EXISTS ||
          e.response.data.code === StripeAccountCreationErrorCode.STRIPE_ACCOUNT_IS_MISSING)
      ) {
        updateStripeAccount()
        return
      }
      // errorPopup.showDefaultError()
      Sentry.captureException(JSON.stringify(e))
      setLoading(false)
    }
  }

  const updateStripeAccount = async () => {
    try {
      setLoading(true)
      const result = boxId
        ? await paymentService.getUpdateBoxAccountLink()
        : await paymentService.getUpdateUserAccountLink()
      if (result.url) {
        window.open(result.url, "_self")
        return
      }
    } catch (e) {
      console.error(LoggerType.Stripe + "update account ", e)
      Sentry.captureException(JSON.stringify(e))
      setLoading(false)
    }
  }

  return { loading, createStripeAccount, updateStripeAccount }
}

export const useStripePayment = () => {
  const stripeWebviewPromise = useRef<{ resolve: (url: string) => void; reject: (reason?: any) => void } | undefined>(
    undefined,
  )

  const stripePayment = async (url: string, options?: { stripeWebviewStartCallback?: () => void }) => {
    try {
      const resultUrl = await new Promise<string>((resolve, reject) => {
        stripeWebviewPromise.current = { resolve, reject }
        window.open(url, "_self") // TODO return url
        options?.stripeWebviewStartCallback?.()
      })

      if (resultUrl.endsWith("success")) {
        return true
      } else {
        if (!resultUrl.endsWith("cancel")) {
          throw Error("Stripe webview finish with wrong url")
        }
        return false
      }
    } finally {
      stripeWebviewPromise.current = undefined
    }
  }

  return { stripePayment }
}

export const useStripeAvailableCountries = () => {
  const { paymentService } = useServicesContext()

  const { data, isLoading } = useSuspenseQuery({
    queryKey: ["availableStripeCountries"],
    queryFn: () => paymentService.getStripeAvailableCountries(),
  })

  // Ensure that data.countriesByPlatform has no undefined records
  const cleanedData = Object.keys(data.countriesByPlatform).map((stripePlatform) => ({
    stripePlatform: stripePlatform as StripePlatform,
    countries: data.countriesByPlatform[stripePlatform as StripePlatform] ?? [],
  }))
  return { availableStripeCountries: cleanedData, loading: isLoading }
}
